import { zodResolver } from '@hookform/resolvers/zod';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { IconButton, Tooltip } from '@mui/material';
import type { FC } from 'react';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import ErrorSection from 'components/ErrorSection/ErrorSection';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import type { NoopT } from 'declarations/types/common';
import { formatTooltipWithShortcut } from 'helpers/formatTooltipWithShortcut';
import { CloseIcon, SendIcon } from 'icons';

import { useMessages } from '../../contexts/MessagesContext';

import type { NewTicketMessageFormValuesT } from './declarations';
import useMessageForm from './hooks/useMessageForm';
import {
  CloseIconBtnStyled,
  FormWrapStyled,
  InputImageWrapStyled,
  InputMessageWrapStyled,
  SendMessageBtnStyled,
  StyledDragDropInput,
  TextAreaStyled,
} from './MessageForm.styled';
import validationSchema from './validationSchema';

export type MessageFormPropsT = {
  roofTopName: string;
  requiresAttachment: boolean;
  assignToWarrCloud: boolean;
  handleOpenAddAttachmentPopup: NoopT;
};

const MessageForm: FC<MessageFormPropsT> = ({
  requiresAttachment,
  assignToWarrCloud,
  handleOpenAddAttachmentPopup,
}) => {
  /**
   * Hooks
   */
  const {
    unsavedMessage,
    isCreatingMessage,
    isMessageCreationError,
    createMessageErrorMessage,
    createMessageValidationErrorMessage,
  } = useMessages();
  const fromMethods = useForm<NewTicketMessageFormValuesT>({
    defaultValues: {
      message: '',
      file: null,
    },
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  const {
    handleSubmitForm,
    previewImage,
    handleImagePaste,
    handleRemoveImage,
    getRootProps,
    getInputProps,
  } = useMessageForm({
    assignToWarrCloud,
    requiresAttachment,
    fromMethods,
  });

  /**
   * Variables
   */
  const { handleSubmit, reset } = fromMethods;

  /**
   * Effects
   */
  useEffect(() => {
    if (unsavedMessage) {
      reset();
    }
  }, [unsavedMessage, reset]);

  return (
    <>
      {isMessageCreationError ? (
        <FlexContainer direction="column" align="center" justify="center">
          <ErrorSection
            errorDetails={createMessageErrorMessage || createMessageValidationErrorMessage}
          />
        </FlexContainer>
      ) : (
        <FormProvider {...fromMethods}>
          <FormWrapStyled>
            <Tooltip title={formatTooltipWithShortcut('Add attachment', 'addAttachment')} arrow>
              <span>
                <IconButton
                  aria-label="Attach"
                  disabled={!requiresAttachment}
                  onClick={handleOpenAddAttachmentPopup}
                  sx={{ color: (t): string => t.palette.primary.main }}
                >
                  <AttachFileIcon />
                </IconButton>
              </span>
            </Tooltip>
            <InputMessageWrapStyled onPaste={handleImagePaste} {...getRootProps()}>
              <TextAreaStyled
                rows={1}
                resizable
                name="message"
                placeholder={'Send a message to Dealer'}
                disabled={isCreatingMessage}
              />

              {previewImage && (
                <InputImageWrapStyled>
                  <img
                    src={previewImage}
                    alt="Preview"
                    style={{ maxHeight: '100px', maxWidth: '100%' }}
                  />
                  <CloseIconBtnStyled
                    variant="transparent"
                    icon={<CloseIcon />}
                    title="Remove image"
                    onClick={handleRemoveImage}
                  />
                </InputImageWrapStyled>
              )}
              <StyledDragDropInput {...getInputProps()} />
            </InputMessageWrapStyled>
            <SendMessageBtnStyled
              type="button"
              aria-label="Create message"
              variant="outline"
              icon={<SendIcon />}
              title="Send a message"
              onClick={handleSubmit(handleSubmitForm)}
            />
          </FormWrapStyled>
        </FormProvider>
      )}
    </>
  );
};

export default MessageForm;
